import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { AxiosError } from 'axios'
import { showBanner } from '../../global-state/banner-slice'
import {
  comingSoonAdminServices,
  type ComingSoonFormData,
} from '../../services/coming-soon-admin-service'
import { opportunityServices } from '../../services/opportunity-service'
import {
  COMING_SOON_MAX_IMAGE_BYTES,
  isValidComingSoonImageType,
  isWithinMaxFileBytes,
} from '../../lib/utils/helpers'
import { ComingSoonFormPresenter } from './coming-soon-form-presenter'

const defaultValues: ComingSoonFormData = {
  cover_image: '',
  title: '',
  description: '',
  sort_order: 0,
  is_published: true,
}

export function ComingSoonFormContainer() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const form = useForm<ComingSoonFormData>({
    defaultValues,
    mode: 'onBlur',
  })

  const [coverPreview, setCoverPreview] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!id) return
    const load = async () => {
      try {
        setIsLoading(true)
        const row = await comingSoonAdminServices.getById(id)
        form.reset({
          cover_image: row.cover_image ?? '',
          title: row.title ?? '',
          description: row.description ?? '',
          sort_order: row.sort_order ?? 0,
          is_published: row.is_published ?? true,
        })
        if (row.cover_image) {
          const keyForPreview = row.cover_image.replace(/\//g, '%2F')
          const preview = await opportunityServices.previewFile(keyForPreview)
          if (preview?.data && preview.status === 200 && preview.data.url) {
            setCoverPreview(preview.data.url as string)
          }
        }
      } catch {
        dispatch(showBanner({ text: 'Failed to load item', variant: 'error-banner' }))
      } finally {
        setIsLoading(false)
      }
    }
    void load()
  }, [id, form, dispatch])

  const onFileUpload = async (file: File) => {
    if (!isValidComingSoonImageType(file)) {
      dispatch(showBanner({ text: 'Only JPEG/PNG images are supported', variant: 'error-banner' }))
      return
    }
    if (!isWithinMaxFileBytes(file, COMING_SOON_MAX_IMAGE_BYTES)) {
      dispatch(
        showBanner({
          text: 'File size must be under 10 MB',
          variant: 'error-banner',
        }),
      )
      return
    }
    const res: { data?: { key?: string }; status?: number } =
      await comingSoonAdminServices.uploadFile(file)
    if (res?.data?.key) {
      form.setValue('cover_image', res.data.key, { shouldValidate: true, shouldDirty: true })
      setCoverPreview(URL.createObjectURL(file))
    } else {
      dispatch(showBanner({ text: 'Failed to upload image', variant: 'error-banner' }))
    }
  }

  const onSubmit = async (data: ComingSoonFormData) => {
    try {
      setIsSubmitting(true)
      if (isEditMode && id) {
        await comingSoonAdminServices.update(id, {
          cover_image: data.cover_image,
          title: data.title,
          description: data.description,
          sort_order: data.sort_order,
          is_published: data.is_published,
        })
        dispatch(showBanner({ text: 'Updated', variant: 'success-banner' }))
      } else {
        await comingSoonAdminServices.create(data)
        dispatch(showBanner({ text: 'Created', variant: 'success-banner' }))
      }
      navigate('/coming-soon')
    } catch (err: unknown) {
      const message =
        err instanceof AxiosError &&
        err.response?.status === 400 &&
        err.response.data &&
        typeof (err.response.data as { message?: unknown }).message === 'string'
          ? (err.response.data as { message: string }).message
          : 'Save failed'
      dispatch(showBanner({ text: message, variant: 'error-banner' }))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ComingSoonFormPresenter
      form={form}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      coverPreview={coverPreview}
      onSubmit={onSubmit}
      onFileUpload={onFileUpload}
    />
  )
}
